import {
  Box,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useDeepCompareMemo } from 'use-deep-compare';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
import { StageCountDatasetValue } from '../../../blocks/BlockPointsMap/controls/StageDataDisplay/types';

type Props = {
  dataset: StageCountDatasetValue[];
  /**
   * The label for the total count.
   * This component handles the translation of the label.
   */
  totalLabel?: string;
  /**
   * Optional total count override. If provided, percentages are computed using this value
   * instead of summing the dataset. Useful when the dataset is a subset of a larger whole.
   */
  totalCount?: number;
  variant?: 'default' | 'compact';
};

export const StagesCountDataDisplay = ({
  dataset,
  totalLabel = 'Total',
  totalCount,
  variant = 'default',
}: Props) => {
  const { t } = useTranslation();

  const orderedCountData = useDeepCompareMemo<StageCountDatasetValue[]>(() => {
    return [...dataset].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [dataset]);

  const computedTotalCount =
    typeof totalCount === 'number'
      ? totalCount
      : dataset.reduce((sum, item) => sum + item.value, 0);

  const defaultTotalSection = (
    <Stack>
      <Typography variant='body2' color='textSecondary'>
        {t(totalLabel)}
      </Typography>
      <Typography variant='h6' sx={{ fontWeight: 600 }}>
        {formatCountForDisplay(computedTotalCount)}
      </Typography>
    </Stack>
  );

  const compactTotalSection = (
    <TableRow>
      <TableCell sx={{ minWidth: LABEL_COLUMN_MIN_WIDTH_PX }}>
        <Typography
          variant='body2'
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {t(totalLabel)}
        </Typography>
      </TableCell>
      <TableCell
        sx={{ minWidth: COUNT_COLUMN_MIN_WIDTH_PX, textAlign: 'right' }}
      >
        <StyledText>{formatCountForDisplay(computedTotalCount)}</StyledText>
      </TableCell>
      <TableCell
        sx={{ minWidth: PERCENT_COLUMN_MIN_WIDTH_PX, textAlign: 'right' }}
      >
        <StyledText>{'100%'}</StyledText>
      </TableCell>
    </TableRow>
  );

  return (
    <Table
      sx={{
        [`& .${tableCellClasses.root}`]: {
          padding: '0px',
          borderBottom: 'none',
        },
      }}
    >
      <TableHead>
        {variant === 'default' ? defaultTotalSection : compactTotalSection}
      </TableHead>
      <TableBody>
        {orderedCountData.map((item) => {
          const displayCount =
            item.value !== 0 ? Math.round(item.value) : item.value;
          const percentage =
            computedTotalCount > 0
              ? `${((displayCount / computedTotalCount) * 100).toFixed(1)}%`
              : '0%';

          return (
            <TableRow key={item.id}>
              <TableCell>
                <Stack
                  direction='row'
                  spacing={2}
                  sx={{ alignItems: 'center', width: '100%' }}
                >
                  <Box
                    sx={{
                      backgroundColor: item.color,
                      height: COLOR_BOX_SIZE,
                      width: COLOR_BOX_SIZE,
                      borderRadius: '0.125rem',
                    }}
                  />
                  <StyledText
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {item.label}
                  </StyledText>
                </Stack>
              </TableCell>
              <TableCell sx={{ textAlign: 'right' }}>
                <StyledText color='textSecondary'>
                  {formatCountForDisplay(displayCount)}
                </StyledText>
              </TableCell>
              <TableCell sx={{ textAlign: 'right' }}>
                <StyledText color='textSecondary'>{percentage}</StyledText>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

const StyledText = styled(Typography)({
  fontSize: '0.75rem',
  fontWeight: 500,
  lineHeight: '166%',
  whiteSpace: 'nowrap',
});

// Fixed widths for numeric columns to keep alignment stable
const LABEL_COLUMN_MIN_WIDTH_PX = '80px';
const COUNT_COLUMN_MIN_WIDTH_PX = '80px';
const PERCENT_COLUMN_MIN_WIDTH_PX = '50px';

// Visual constants used to keep rows visually consistent
const COLOR_BOX_SIZE = '0.5rem';

function formatCountForDisplay(count: number): string {
  return count.toLocaleString();
}
